import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation } from 'react-router';
import React, { useEffect } from 'react';
import { preload } from 'react-dom';
import { createTheme, GlobalStyles, ThemeProvider } from '@mui/material';
import { themeConfig } from './utils/theme.js';
import { esES } from '@mui/material/locale';
import { detectLang, initI18n, loadLanguage } from './i18n.js';
import TagManager from 'react-gtm-module';
import { captureAdParams } from './utils/ads.js';
import { trackClicks } from './utils/analytics.js';
import { langs, buildPageMeta, findSlugKey } from './utils/languageUtils.js';
import { canonicalPath, siteOrigin } from './utils/siteUrl.js';
import { slugs } from './hooks/slugs.js';
import { WhatsAppFab } from './components/WhatsAppFab.jsx';
import { POK_NAME } from './constants.js';
import { blogPostsBase } from './utils/blogUtils.js';
import { FONTS } from './components/sections/theme.js';
import fontFaces from '../public/fonts.css?raw';

const theme = createTheme(themeConfig, esES);

const ADOPT_WEBSITE_CODE = 'e00b29f9-ea97-4fca-a238-2e4161872816';
// Adopt's banner enters with a slide that grows its own children, which shifts them on every frame.
const ADOPT_BANNER_CSS = '#cookie-banner,#cookie-banner *{animation:none!important;transition:none!important}';
// The weights a page's first screen renders; any other face loads on demand.
const GEIST_WEIGHTS = [400, 500, 600, 700];

const preloadFont = (href) => preload(href, { as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' });

function whenIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(callback, { timeout: 2500 });
        return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(callback, 2500);
    return () => window.clearTimeout(id);
}

function injectAdopt() {
    if (document.querySelector('script.adopt-injector')) return;
    const script = document.createElement('script');
    script.src = `https://tag.goadopt.io/injector.js?website_code=${ADOPT_WEBSITE_CODE}`;
    script.className = 'adopt-injector';
    script.async = true;
    document.body.appendChild(script);
}

void import('@pok/sentry-config/browser').then(({ sentryBrowserInit }) => {
    sentryBrowserInit({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        enableLogs: true,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    });
});

export const clientMiddleware = [
    async ({ request }) => {
        try {
            await loadLanguage(detectLang(new URL(request.url).pathname));
        } catch {
            // A document load fetches the current build; the outgoing page stays until it lands.
            window.location.assign(request.url);
            await new Promise(() => {});
        }
    },
];

export default function Root() {
    const location = useLocation();
    const i18n = initI18n(location);
    const pathname = canonicalPath(location.pathname);
    const isBlog = pathname.startsWith('/blog');
    const links = isBlog ? [] : createLinks(pathname);
    const metas = isBlog ? [] : createMeta(pathname, i18n.t);
    GEIST_WEIGHTS.forEach((weight) => preloadFont(`/fonts/geist-${weight}.woff2`));
    const isBlogPost = langs.some((lang) => pathname.startsWith(`${blogPostsBase(lang)}/`));
    if (!isBlogPost) preloadFont('/fonts/geist-mono-500.woff2');

    useEffect(() => {
        // GTM and Adopt load once the browser is idle, each in its own idle period, so neither
        // competes with LCP or with the first interaction. Consent Mode's default comes from
        // Adopt's template inside the GTM container, which runs before any other tag there.
        const initGtm = () =>
            TagManager.initialize({
                gtmId: 'GTM-5LTF29X3',
                auth: import.meta.env.VITE_GTM_AUTH,
                preview: import.meta.env.VITE_GTM_PREVIEW,
            });

        let cancelAdopt = () => {};
        const cancelGtm = whenIdle(() => {
            initGtm();
            cancelAdopt = whenIdle(injectAdopt);
        });
        return () => {
            cancelGtm();
            cancelAdopt();
        };
    }, []);

    useEffect(() => {
        captureAdParams(location.search);
    }, [location.search]);

    useEffect(trackClicks, []);

    return (
        <html lang={i18n.language}>
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="emotion-insertion-point" content="" />
                <meta name="adopt-website-id" content={ADOPT_WEBSITE_CODE} />
                {!isBlog && <meta property="og:type" content="website" />}
                <link rel="dns-prefetch" href="https://www.googletagmanager.com/" />
                <link rel="icon" type="image/x-icon" href="/images/favicon.ico" />

                <style dangerouslySetInnerHTML={{ __html: fontFaces + ADOPT_BANNER_CSS }} />
                <Links />
                <Meta />
                {metas.map((meta, index) => {
                    if (meta.title) {
                        return <title key={'title'}>{meta.title}</title>;
                    }
                    return <meta key={'meta' + index} {...meta} />;
                })}
                {links.map((link, index) => {
                    const { key, ...props } = link;
                    return <link key={key || index} {...props} />;
                })}
            </head>
            <body>
                <ThemeProvider theme={theme}>
                    <GlobalStyles
                        styles={{
                            '*': {
                                margin: 0,
                                padding: 0,
                                boxSizing: 'border-box',
                                fontFamily: FONTS.sans,
                            },
                            body: { overflowX: 'clip' },
                            html: { overflowX: 'clip' },
                        }}
                    />
                    <Outlet />
                    <WhatsAppFab />
                    <ScrollRestoration />

                    <Scripts />
                </ThemeProvider>
            </body>
        </html>
    );
}

function createLinks(pathname) {
    const baseUrl = siteOrigin();
    const canonicalUrl = pathname === '' ? `${baseUrl}/en` : `${baseUrl}${pathname}`;
    const links = [];
    links.push({ rel: 'canonical', href: canonicalUrl });
    const key = findSlugKey(pathname);
    // Utility/ads routes outside the slug map must not claim the home hreflang cluster.
    if (key === null) return links;

    if (key === 'home') {
        // LCP candidate: the hero animation poster (the video itself uses preload="metadata").
        links.push({
            rel: 'preload',
            as: 'image',
            href: '/images/home/hero-anim-poster.webp',
            type: 'image/webp',
            fetchPriority: 'high',
        });
    }

    langs.forEach((lang) => {
        const langSlug = slugs[key]?.[lang];
        if (langSlug == null) return;
        const hreflangHref = langSlug ? `${baseUrl}/${lang}/${langSlug}` : `${baseUrl}/${lang}`;
        links.push({ key: lang, rel: 'alternate', hrefLang: lang, href: hreflangHref });
    });

    const defaultSlug = slugs[key]?.['en'];
    if (defaultSlug != null) {
        const defaultHref = defaultSlug ? `${baseUrl}/en/${defaultSlug}` : `${baseUrl}/en`;
        links.push({ rel: 'alternate', hrefLang: 'x-default', href: defaultHref });
    }
    return links;
}

function createMeta(pathname, translate) {
    const baseUrl = siteOrigin();
    const key = findSlugKey(pathname);
    // Utility/ads routes outside the slug map: noindex instead of inheriting
    // the home metadata (duplicate titles confuse crawlers and answer engines).
    if (key === null) {
        return [{ title: POK_NAME }, { name: 'robots', content: 'noindex' }];
    }
    return buildPageMeta(
        translate(`metas.${key}.title`),
        translate(`metas.${key}.description`),
        `${baseUrl}${pathname}`,
        'https://www.pok.tech/images/og/og_image.png',
    );
}
